import React, { useEffect, useMemo, useState } from 'react';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  createMedcallImlaiDueDateDrafts,
  createMedcallImlaiDueDateUpdate,
  createMedcallImlaiDueDateUpdates,
  getMedcallImlaiDateLowerBound,
  getMedcallImlaiDueDateReasonLabel,
  getMedcallImlaiDueDateWarnings,
  getMedcallImlaiMedicationDose,
  getMedcallImlaiMedicationFrequency,
  getMedcallImlaiMedicationName,
  normalizeMedcallImlaiDate,
} from './medcallImlaiDueDateWarning';
import type {
  MedcallImlaiCountyHoliday,
  MedcallImlaiDueDateDrafts,
  MedcallImlaiDueDateMedication,
  MedcallImlaiDueDateUpdate,
} from './medcallImlaiDueDateWarning';

export type MedcallImlaiDueDateWarningProps = {
  countyHolidays: MedcallImlaiCountyHoliday[];
  medications: MedcallImlaiDueDateMedication[];
  onCancel: () => void;
  onUpdateAll: (updates: MedcallImlaiDueDateUpdate[]) => Promise<void> | void;
  onUpdateDueDate: (update: MedcallImlaiDueDateUpdate) => Promise<void> | void;
  open: boolean;
  pending?: boolean;
  today?: string;
};

const MedicationHeader = ({ medication }: { medication: MedcallImlaiDueDateMedication }) => {
  return (
    <div className="medication-header text-left">
      <div className="medication-header-body space-y-1">
        <div className="medication-name">
          <span className="medication-label font-semibold">Medication: </span>
          {getMedcallImlaiMedicationName(medication)}
        </div>
        <div className="medication-name">
          <span className="medication-label font-semibold">Dose: </span>
          {getMedcallImlaiMedicationDose(medication)}
        </div>
        <div className="medication-route">
          <span className="medication-label font-semibold">Route: </span>
          {medication.route}
        </div>
        <div className="medication-frequency">
          <span className="medication-label font-semibold">Frequency: </span>
          {getMedcallImlaiMedicationFrequency(medication)}
        </div>
      </div>
    </div>
  );
};

export const MedcallImlaiDueDateWarning = ({
  countyHolidays,
  medications,
  onCancel,
  onUpdateAll,
  onUpdateDueDate,
  open,
  pending = false,
  today,
}: MedcallImlaiDueDateWarningProps) => {
  const minimumDate = normalizeMedcallImlaiDate(today) || getMedcallImlaiDateLowerBound();
  const warnings = useMemo(() => {
    return getMedcallImlaiDueDateWarnings(medications, countyHolidays, minimumDate);
  }, [countyHolidays, medications, minimumDate]);
  const initialDrafts = useMemo(() => createMedcallImlaiDueDateDrafts(warnings), [warnings]);
  const serializedInitialDrafts = JSON.stringify(initialDrafts);
  const [drafts, setDrafts] = useState<MedcallImlaiDueDateDrafts>(initialDrafts);
  const [pendingMedicationId, setPendingMedicationId] = useState<number | null>(null);
  const [updatingAll, setUpdatingAll] = useState(false);

  useEffect(() => {
    setDrafts(JSON.parse(serializedInitialDrafts) as MedcallImlaiDueDateDrafts);
  }, [serializedInitialDrafts, open]);

  const locallyPending = updatingAll || pendingMedicationId !== null;
  const updatePending = pending || locallyPending;
  const hasChanges = warnings.some((warning) => {
    return drafts[warning.medication.id] !== warning.dueDate;
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen || updatePending) {
      return;
    }

    onCancel();
  };

  const handleDateChange = (medicationId: number, value: string) => {
    const dueDate = normalizeMedcallImlaiDate(value);
    if (!dueDate || dueDate < minimumDate) {
      return;
    }

    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [medicationId]: dueDate,
    }));
  };

  const handleUpdateDueDate = async (medicationId: number) => {
    if (updatePending) return;
    const warning = warnings.find((candidate) => candidate.medication.id === medicationId);
    if (!warning) {
      return;
    }

    const dueDate = drafts[medicationId] || warning.dueDate;
    if (dueDate === warning.dueDate || dueDate < minimumDate) {
      return;
    }

    setPendingMedicationId(medicationId);
    try {
      await onUpdateDueDate(createMedcallImlaiDueDateUpdate(warning, dueDate));
    } finally {
      setPendingMedicationId(null);
    }
  };

  const handleUpdateAll = async () => {
    if (!hasChanges || updatePending) {
      return;
    }

    setUpdatingAll(true);
    try {
      await onUpdateAll(createMedcallImlaiDueDateUpdates(warnings, drafts));
    } finally {
      setUpdatingAll(false);
    }
  };

  if (warnings.length < 1) {
    return null;
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent
        className="IMLAIDueDateNotification overflow-hidden p-0"
        contentHeight="auto"
        contentMaxHeight="80vh"
        contentMaxWidth="calc(100vw - 2rem)"
        contentTop="10vh"
        contentWidth="800px"
      >
        <div aria-busy={updatePending} className="flex max-h-[80vh] min-h-0 flex-col">
          <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4 pr-10">
            <DialogTitle>Some medications have due date in holiday / weekend</DialogTitle>
            <DialogDescription className="sr-only">
              Update future IM-LAI medication due dates that fall on a county holiday or weekend.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <Accordion className="border border-[var(--neutral-gray-40)]" type="multiple">
              {warnings.map((warning) => {
                const medication = warning.medication;
                const medicationId = medication.id;
                const dueDate = drafts[medicationId] || warning.dueDate;
                const unchanged = dueDate === warning.dueDate;
                return (
                  <AccordionItem
                    className="med-collapse px-4 last:border-b-0"
                    key={`med-${medicationId}`}
                    value={`med-${medicationId}`}
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <MedicationHeader medication={medication} />
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid items-end gap-3 border border-[var(--neutral-gray-40)] p-3 md:grid-cols-[1fr_1fr_auto]">
                        <div>
                          <div className="text-sm font-semibold">Due Date</div>
                          <div>{getMedcallImlaiDueDateReasonLabel(warning)}</div>
                        </div>
                        <input
                          aria-label={`Due Date for ${getMedcallImlaiMedicationName(medication)}`}
                          className="h-[var(--control-height-standard)] w-full rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-white px-3 text-sm"
                          disabled={updatePending}
                          min={minimumDate}
                          onChange={(event) => handleDateChange(medicationId, event.target.value)}
                          type="date"
                          value={dueDate}
                        />
                        <Button
                          disabled={unchanged || updatePending}
                          onClick={() => handleUpdateDueDate(medicationId)}
                          type="button"
                        >
                          Update Due Date
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>

          <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
            <Button disabled={updatePending} onClick={onCancel} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={!hasChanges || updatePending} onClick={handleUpdateAll} type="button">
              Update All
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
